/**
 * 표 하단 페이지 번호 배치 — 목업 `.pager .nav`(`1 2 3 4 … 31`).
 *
 * 번호를 전부 그리지 않는 이유: 문서 목록은 10건씩이라 수백 건이면 페이지가 수십 개가 된다
 * (결재함은 전량을 그리지만 그쪽은 결재 대기 건이라 페이지가 몇 개 안 된다). 그래서
 * **현재 위치 주변 창 + 양끝**만 남기고 사이를 생략 표시로 접는다.
 *
 * 순수 함수로 뺀 이유는 경계(1페이지·마지막 페이지·창보다 짧은 총 페이지)가 눈으로 확인하기
 * 번거로운 자리라서다 — 여기만 spec으로 고정하면 JSX 쪽은 배열을 그리기만 하면 된다.
 */

/** 페이지 번호 또는 생략 표시. */
export type PagerSlot = number | 'gap';

/**
 * 현재 페이지 주변 `windowSize`개 + 첫/마지막 페이지를 담은 배치를 만든다.
 *
 * - 총 페이지가 창+양끝보다 짧으면 생략 없이 전부 그린다(생략이 오히려 자리를 먹는다).
 * - `page`가 범위를 벗어나면 clamp한다 — `?page=` 쿼리로 직접 들어오는 값이라 신뢰할 수 없다.
 * - 생략은 **건너뛴 페이지가 2개 이상일 때만** 넣는다. 1개면 `…`가 번호보다 넓어 손해다.
 */
export function buildPagerRange(page: number, totalPages: number, windowSize = 5): PagerSlot[] {
  if (!Number.isFinite(totalPages) || totalPages < 1) return [];

  const total = Math.floor(totalPages);
  const current = Math.min(Math.max(Math.floor(page) || 1, 1), total);
  const size = Math.max(1, Math.floor(windowSize));

  // 창 + 양끝(2)보다 짧으면 전부.
  if (total <= size + 2) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const start = Math.min(Math.max(current - Math.floor(size / 2), 1), total - size + 1);
  const end = start + size - 1;

  const slots: PagerSlot[] = [];
  if (start > 1) {
    slots.push(1);
    // 건너뛰는 게 한 페이지뿐이면 생략 대신 그 번호를 그린다.
    if (start === 3) slots.push(2);
    else if (start > 3) slots.push('gap');
  }
  for (let p = start; p <= end; p++) slots.push(p);
  if (end < total) {
    if (end === total - 2) slots.push(total - 1);
    else if (end < total - 2) slots.push('gap');
    slots.push(total);
  }
  return slots;
}
